import React, { useEffect, useRef, useState, useMemo } from "react";
import { motion } from "framer-motion";
import { vimeoBgUrl, projectPoster } from "@/lib/vimeo";
import { SITE } from "@/constants/testIds";

// Curator-priority clips: rotate these Vimeo IDs at the top of the hero reel
// before the DB-driven featured films kick in. Add/remove IDs here to swap
// the intro loops without a database migration.
const HERO_PRIORITY_CLIPS = [
  { vimeo_id: "1209024119", title: "GIVA · Exchange Fest · Film 1", aspect: "16:9" },
  { vimeo_id: "1209024118", title: "GIVA · Exchange Fest · Film 2", aspect: "16:9" },
];

function HeroShowreel({ projects = [] }) {
  // Priority clips first, then any 16:9 featured DB projects. Dedupe by
  // vimeo_id in case a curator later promotes one of the priority clips to a
  // full project record.
  const reel = useMemo(() => {
    const dbClips = projects.filter((p) => (p.aspect || "16:9") === "16:9");
    const merged = [...HERO_PRIORITY_CLIPS, ...dbClips];
    const seen = new Set();
    return merged.filter((p) => {
      if (seen.has(p.vimeo_id)) return false;
      seen.add(p.vimeo_id);
      return true;
    }).slice(0, 4);   // hard cap — more clips = more poster fetches, no visual benefit
  }, [projects]);
  const safe = reel.length ? reel : projects.slice(0, 1);
  const [active, setActive] = useState(0);
  // Which clip indices are currently in the DOM. On rotation we add the new
  // one immediately (so its player has ~1.8s to buffer during the crossfade)
  // and prune the outgoing one right after the crossfade completes. That
  // keeps the iframe count at 1 most of the time, and briefly 2 during the
  // 1.8s visual crossfade — never accumulating over multiple rotations.
  const CROSSFADE_MS = 1800;
  const [mounted, setMounted] = useState(() => new Set([0]));
  const t = useRef(null);
  const prevActiveRef = useRef(0);

  useEffect(() => {
    if (safe.length <= 1) return;
    t.current = setInterval(() => setActive((i) => (i + 1) % safe.length), 10000);
    return () => clearInterval(t.current);
  }, [safe.length]);

  // Add the newly-active clip to `mounted`, then unmount the previous clip
  // after the crossfade completes. This is the fix for the accumulating-
  // iframes bug — previously the Set grew forever, keeping every played
  // clip's Vimeo player streaming (opacity 0) in the background.
  useEffect(() => {
    const prev = prevActiveRef.current;
    prevActiveRef.current = active;

    setMounted((s) => {
      if (s.has(active)) return s;
      const n = new Set(s);
      n.add(active);
      return n;
    });

    if (prev === active) return undefined;
    const t2 = setTimeout(() => {
      setMounted((s) => {
        if (!s.has(prev)) return s;
        const n = new Set(s);
        n.delete(prev);
        return n;
      });
    }, CROSSFADE_MS + 200);
    return () => clearTimeout(t2);
  }, [active]);

  if (!safe.length) return <div className="absolute inset-0 bg-charcoal" />;
  return (
    <div className="absolute inset-0 overflow-hidden">
      {safe.map((p, i) => (
        <div
          key={p.vimeo_id}
          className="absolute inset-0 transition-opacity duration-[1800ms] ease-in-out"
          style={{ opacity: i === active ? 1 : 0 }}
        >
          {/* Poster shows instantly while the Vimeo player buffers */}
          <img
            src={projectPoster(p)}
            alt=""
            aria-hidden="true"
            className="absolute inset-0 w-full h-full object-cover"
            loading={i === 0 ? "eager" : "lazy"}
            fetchPriority={i === 0 ? "high" : "auto"}
            decoding="async"
          />
          {mounted.has(i) && (
            <div className="vimeo-cover-hero">
              <iframe
                title={`Relatable Studio showreel ${i + 1}`}
                aria-hidden="true"
                src={vimeoBgUrl(p.vimeo_id)}
                allow="autoplay; fullscreen"
                loading="eager"
              />
            </div>
          )}
        </div>
      ))}
    </div>
  );
}

export default function Hero({ projects = [] }) {
  return (
    <section data-testid={SITE.heroRoot} className="relative h-[100svh] min-h-[640px] w-full overflow-hidden bg-ink">
      <HeroShowreel projects={projects} />
      {/* Subtle gradient — keeps video as the visual lead */}
      <div className="absolute inset-0 bg-gradient-to-b from-ink/55 via-ink/15 to-ink/80" />
      <div className="absolute inset-0 bg-ink/20 mix-blend-multiply" />

      {/* Top meta row */}
      <div className="absolute top-0 left-0 right-0 z-10 pt-28 md:pt-32 px-6 md:px-12 lg:px-24 flex justify-between items-start">
        <motion.span
          initial={{ opacity: 0, y: -12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4, duration: 0.7 }}
          className="eyebrow text-bone/85"
        >
          ◐ Storytelling · Production · Brand Experience
        </motion.span>
        <motion.span
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.7, duration: 0.7 }}
          className="eyebrow text-bone/70 hidden md:inline-block"
        >
          Bangalore — India
        </motion.span>
      </div>

      {/* Lower-third — headline only, no CTAs (kept clean per brief) */}
      <div className="absolute inset-x-0 bottom-[88px] md:bottom-[96px] z-10 px-6 md:px-12 lg:px-24">
        <motion.h1
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1, duration: 0.5, ease: [0.7, 0, 0.2, 1] }}
          className="font-display text-bone text-4xl sm:text-5xl md:text-6xl lg:text-7xl leading-[1.0] tracking-[-0.03em] font-medium max-w-[20ch]"
        >
          We solve problems before we pick up a <span className="text-relatable">camera.</span>
        </motion.h1>

        <motion.p
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.35, duration: 0.5 }}
          className="speakable-summary mt-5 md:mt-6 max-w-xl text-bone/85 text-sm md:text-base leading-relaxed"
        >
          Content, campaigns and experiences built around real business challenges. Formerly Straight Arrow Studios.
        </motion.p>
      </div>

      {/* Bottom marquee — proper separation from CTAs */}
      <div className="absolute bottom-0 left-0 right-0 z-10 border-t border-line/60 bg-ink/55 backdrop-blur-md">
        <div className="overflow-hidden py-3">
          <div className="marquee-track gap-12 whitespace-nowrap">
            {["marquee-copy-1", "marquee-copy-2"].map((copyId) => (
              <div className="flex items-center gap-12 pr-12" key={copyId}>
                {[
                  "1600+ Projects Delivered",
                  "300+ Clients Served",
                  "700+ Creative Partners",
                  "Bangalore — Working Across India",
                  "Formerly Straight Arrow Studios — Est. 2020",
                ].map((t) => (
                  <span key={t} className="eyebrow text-bone/75">◇ {t}</span>
                ))}
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
